/**
 * User Profile page
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './UserProfile.css';

interface UserData {
    id: number;
    username: string;
    email: string;
    verified_email: number;
}

export default function UserProfile() {
    const { userId } = useParams<{ userId: string }>();
    const { user: currentUser } = useAuth();
    const [userData, setUserData] = useState<UserData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await fetch(`/api/users/${userId}`);
                const data = await response.json();

                if (response.ok) {
                    setUserData(data.user);
                } else {
                    setError(data.error || 'Failed to load user profile');
                }
            } catch (err) {
                setError('An error occurred while loading the profile');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchUserData();
        }
    }, [userId]);

    if (loading) {
        return (
            <div className="userProfileContainer">
                <div className="userProfileCard">
                    <div className="userProfileLoading">Loading...</div>
                </div>
            </div>
        );
    }

    if (error || !userData) {
        return (
            <div className="userProfileContainer">
                <div className="userProfileCard">
                    <div className="userProfileError">
                        <h2>Error</h2>
                        <p>{error || 'User not found'}</p>
                    </div>
                </div>
            </div>
        );
    }

    const isOwnProfile = currentUser?.id === userData.id;
    const isEmailVerified = userData.verified_email === 1;

    return (
        <div className="userProfileContainer">
            <div className="userProfileCard">
                <div className="userProfileHeader">
                    <div className="userProfileAvatar">
                        {userData.username.charAt(0).toUpperCase()}
                    </div>
                    <h1>{userData.username}</h1>
                </div>

                <div className="userProfileContent">
                    <div className="userProfileSection">
                        <h2>Account Information</h2>

                        <div className="userProfileField">
                            <label>Username</label>
                            <div className="userProfileValue">
                                {userData.username} (#{userData.id})
                            </div>
                        </div>

                        {isOwnProfile && (
                            <>
                                <div className="userProfileField">
                                    <label>Email</label>
                                    <div className="userProfileValue">
                                        {userData.email}
                                        {isEmailVerified ? (
                                            <span className="userProfileVerifiedBadge">
                                                ✓ Verified
                                            </span>
                                        ) : (
                                            <span className="userProfileUnverifiedBadge">
                                                ✗ Not Verified
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
